/**
 * Import d'une base KeePass **KDBX 4 et 3.1** (`.kdbx`), format natif de
 * KeePass 2 / KeePassXC / KeePassDX (R7).
 *
 * Purement local : les octets choisis par l'utilisateur sont dechiffres sur
 * l'appareil puis transformes en `ImportedEntry` ; aucune E/S reseau, aucun
 * secret journalise. Le mot de passe de la base n'est ni conserve ni transmis.
 *
 * INVARIANT CLAUDE.md : **aucune crypto maison**. Toutes les primitives
 * viennent de `@noble/hashes` / `@noble/ciphers` (memes bibliotheques auditees
 * que `@klavox/crypto-core`) : Argon2d/Argon2id, AES-256 (CBC, et ECB pour
 * l'AES-KDF), ChaCha20, HMAC-SHA-256, SHA-256/512.
 *
 * Le format est lu EXACTEMENT comme la specification KDBX 4 le decrit :
 *   en-tete -> HMAC d'en-tete (verifie AVANT tout dechiffrement, c'est lui qui
 *   detecte un mauvais mot de passe) -> blocs HMAC-SHA-256 (chaque bloc
 *   authentifie separement) -> dechiffrement -> GZip -> en-tete interne
 *   (binaires + cle du flux ChaCha20 des valeurs protegees) -> XML.
 *
 * Perimetre : KDBX 4 (4.0 et 4.1), chiffrement AES-256-CBC ou ChaCha20,
 * derivation Argon2d / Argon2id / AES-KDF, fichier-cle optionnel.
 *
 * KDBX 3 (3.0 et 3.1) : ce qu'ecrit KeePass 2 jusqu'a la 2.34, et encore
 * aujourd'hui pour toute base restee en AES-KDF (KeePass n'ecrit le KDBX 4 que
 * si une option l'exige). Pas de HMAC : en-tete aux longueurs sur 16 bits,
 * AES-KDF dans les champs 5/6, charge AES-256-CBC commencant par les octets de
 * controle de l'en-tete (ce sont EUX qui detectent un mauvais mot de passe),
 * blocs haches SHA-256, GZip, puis le XML directement ; valeurs protegees par
 * Salsa20 (ou ChaCha20), pieces jointes dans `Meta > Binaries`, empreinte de
 * l'en-tete verifiee par `Meta > HeaderHash`.
 *
 * KDBX 2.x et Twofish sont refuses avec un message explicite (jamais un
 * dechiffrement approximatif).
 *
 * Lit aussi la base **KeePass 1.x** (`.kdb`, KeePass 1.x / KeePassX 0.4,
 * `readKdbFile`) : en-tete fixe, AES-KDF, AES-256-CBC, SHA-256 du contenu,
 * groupes a niveaux et entrees en champs types.
 *
 * Lit aussi l'export **KeePass XML (2.x)** en clair (`parseKeePassXml`) : meme
 * document, meme parcours d'entrees (`readDocument`), sans dechiffrement.
 */
import { argon2dAsync, argon2idAsync } from '@noble/hashes/argon2.js';
import { hmac } from '@noble/hashes/hmac.js';
import { sha256, sha512 } from '@noble/hashes/sha2.js';
import { cbc, unsafe as aesBlock } from '@noble/ciphers/aes.js';
import { chacha20 } from '@noble/ciphers/chacha.js';
import { salsa20 } from '@noble/ciphers/salsa.js';
import { base64ToBytes, type EntryCustomField } from '@klavox/crypto-core';
import type { VaultItemType } from '@klavox/vault-model';
import {
  ImportPasswordInvalidError,
  ImportPasswordRequiredError,
  joinNotes,
  normalizeTotpSecret,
  type ImportedAttachment,
  type ImportedEntry,
} from './csv-import.js';
import { parseTagList } from './entry-tags.js';
import { resolveEntryReferences, type ReferenceIndex, type ReferenceTarget } from './keepass-references.js';
import type { PasswordHistoryEntry } from './password-history.js';
import { decompress } from './zip.js';

/** Signature des fichiers KDBX 2/3/4 (KeePass 2.x). */
const KDBX_SIGNATURE_1 = 0x9aa2d903;
const KDBX_SIGNATURE_2 = 0xb54bfb67;

/** Plafonds contre un fichier forge : une base reelle reste tres loin en dessous. */
const MAX_ARGON2_MEMORY_KIB = 1024 * 1024; // 1 Gio
const MAX_ARGON2_ITERATIONS = 100;
const MAX_AES_KDF_ROUNDS = 20_000_000;
/** Tours AES-KDF entre deux rendus de la main au navigateur (~0,1 s sur M1) : l'onglet reste vivant, la progression s'affiche. */
const AES_KDF_TICK = 65_536;
const MAX_PAYLOAD_BYTES = 256 * 1024 * 1024;

/** UUID des primitives KDBX supportees. */
const CIPHER_AES256_CBC = '31c1f2e6-bf71-4350-be58-05216afc5aff';
const CIPHER_CHACHA20 = 'd6038a2b-8b6f-4cb5-a524-339a31dbb59a';
const CIPHER_TWOFISH = 'ad68f29f-576f-4bb9-a36a-d47af965346c';
const KDF_ARGON2D = 'ef636ddf-8c29-444b-91f7-a9a403e30a0c';
const KDF_ARGON2ID = '9e298b19-56db-4773-b23d-fc3ec6f0a1e6';
const KDF_AES = 'c9d9f39a-628a-4460-bf74-0d08c18a4fea';

/** Flux des valeurs protegees (`InnerRandomStreamID`) et nonce fixe de Salsa20 dans KeePass. */
const INNER_STREAM_SALSA20 = 2;
const INNER_STREAM_CHACHA20 = 3;
const SALSA20_NONCE = Uint8Array.from([0xe8, 0x30, 0x09, 0x4b, 0x97, 0x20, 0x5d, 0x2a]);

/** Champs KeePass deja portes par un champ natif de Klavox : pas de doublon en notes. */
const NATIVE_STRING_KEYS = new Set(['Title', 'UserName', 'Password', 'URL', 'Notes']);
/** Cles TOTP des differentes generations de KeePass et de ses greffons. */
const TOTP_STRING_KEYS = ['otp', 'TimeOtp-Secret-Base32', 'TOTP Seed', 'TOTP Secret'];

export class KdbxError extends Error {
  override readonly name = 'KdbxError';
  constructor(message: string) {
    super(`base KeePass illisible : ${message}`);
  }
}

// --- Lecture binaire --------------------------------------------------------

class ByteReader {
  #offset = 0;
  constructor(private readonly bytes: Uint8Array) {}

  get offset(): number {
    return this.#offset;
  }

  take(length: number): Uint8Array {
    if (length < 0 || this.#offset + length > this.bytes.length) throw new KdbxError('fichier tronque');
    const slice = this.bytes.subarray(this.#offset, this.#offset + length);
    this.#offset += length;
    return slice;
  }

  u8(): number {
    return this.take(1)[0]!;
  }

  u16(): number {
    const b = this.take(2);
    return b[0]! | (b[1]! << 8);
  }

  u32(): number {
    const b = this.take(4);
    return (b[0]! | (b[1]! << 8) | (b[2]! << 16) | (b[3]! << 24)) >>> 0;
  }

  u64(): number {
    const low = this.u32();
    const high = this.u32();
    const value = high * 0x1_0000_0000 + low;
    if (!Number.isSafeInteger(value)) throw new KdbxError('valeur 64 bits hors limites');
    return value;
  }
}

function concatBytes(parts: readonly Uint8Array[]): Uint8Array {
  const total = parts.reduce((sum, part) => sum + part.length, 0);
  const out = new Uint8Array(total);
  let offset = 0;
  for (const part of parts) {
    out.set(part, offset);
    offset += part.length;
  }
  return out;
}

function uuidOf(bytes: Uint8Array): string {
  const hex = [...bytes].map((byte) => byte.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

/**
 * Entier 64 bits en petit-boutiste : prefixe des cles et des donnees HMAC de la specification. Au-dela de 2^53, un
 * `number` est arrondi : `0xffff_ffff_ffff_ffff` valait 2^64 et s'ecrivait en huit octets NULS -- la cle HMAC d'en-tete
 * etait celle du bloc 0 et toute vraie base KDBX 4 etait refusee pour « mot de passe incorrect ». D'ou le `bigint`.
 */
function u64le(value: number | bigint): Uint8Array {
  const out = new Uint8Array(8);
  new DataView(out.buffer).setBigUint64(0, BigInt(value), true);
  return out;
}

function u32le(value: number): Uint8Array {
  const out = new Uint8Array(4);
  new DataView(out.buffer).setUint32(0, value, true);
  return out;
}

/** Index de la cle HMAC d'en-tete : `UINT64_MAX` (`ulong.MaxValue` de KeePassLib), huit octets 0xFF. */
const HEADER_HMAC_INDEX = 0xffff_ffff_ffff_ffffn;

/** Cle HMAC du bloc `index` : SHA-512(index || cle de base), specification KDBX 4. */
function blockHmacKey(base: Uint8Array, index: number | bigint): Uint8Array {
  return sha512(concatBytes([u64le(index), base]));
}

function timingSafeEquals(a: Uint8Array, b: Uint8Array): boolean {
  if (a.length !== b.length) return false;
  let diff = 0;
  for (let i = 0; i < a.length; i++) diff |= a[i]! ^ b[i]!;
  return diff === 0;
}

// --- En-tete et parametres --------------------------------------------------

type VariantValue = number | boolean | string | Uint8Array;

/**
 * `VariantDictionary` : conteneur type des parametres de derivation (champ 11
 * de l'en-tete). Format : version u16, puis (type u8, nom, valeur) jusqu'au
 * type 0.
 */
function parseVariantDictionary(bytes: Uint8Array): Map<string, VariantValue> {
  const reader = new ByteReader(bytes);
  const version = reader.u16();
  if (version >> 8 > 1) throw new KdbxError('parametres de derivation en version inconnue');
  const values = new Map<string, VariantValue>();
  const decoder = new TextDecoder();
  for (;;) {
    const type = reader.u8();
    if (type === 0) break;
    const name = decoder.decode(reader.take(reader.u32()));
    const raw = reader.take(reader.u32());
    const inner = new ByteReader(raw);
    switch (type) {
      case 0x04:
      case 0x0c:
        values.set(name, inner.u32());
        break;
      case 0x05:
      case 0x0d:
        values.set(name, inner.u64());
        break;
      case 0x08:
        values.set(name, raw[0] === 1);
        break;
      case 0x18:
        values.set(name, decoder.decode(raw));
        break;
      case 0x42:
        values.set(name, new Uint8Array(raw));
        break;
      default:
        throw new KdbxError(`parametre de derivation de type ${type} inconnu`);
    }
  }
  return values;
}

function requireBytes(params: Map<string, VariantValue>, key: string): Uint8Array {
  const value = params.get(key);
  if (!(value instanceof Uint8Array)) throw new KdbxError(`parametre de derivation ${key} absent`);
  return value;
}

function requireNumber(params: Map<string, VariantValue>, key: string): number {
  const value = params.get(key);
  if (typeof value !== 'number') throw new KdbxError(`parametre de derivation ${key} absent`);
  return value;
}

interface KdbxHeader {
  readonly major: number;
  readonly cipher: string;
  readonly compressed: boolean;
  readonly masterSeed: Uint8Array;
  readonly encryptionIv: Uint8Array;
  readonly kdfParams: Map<string, VariantValue>;
  /** KDBX 3 : flux des valeurs protegees, et premiers octets attendus en tete de la charge dechiffree. */
  readonly streamId: number;
  readonly streamKey: Uint8Array;
  readonly streamStartBytes: Uint8Array;
  /** Octets exacts de l'en-tete, tels qu'ils sont authentifies par le HMAC. */
  readonly raw: Uint8Array;
}

function parseHeader(bytes: Uint8Array): { header: KdbxHeader; reader: ByteReader } {
  const reader = new ByteReader(bytes);
  if (reader.u32() !== KDBX_SIGNATURE_1 || reader.u32() !== KDBX_SIGNATURE_2) {
    throw new KdbxError('signature KeePass absente');
  }
  const minor = reader.u16();
  const major = reader.u16();
  if (major !== 3 && major !== 4) {
    throw new KdbxError(
      major < 3
        ? `format KDBX ${major}.${minor} : reenregistrez la base en KDBX 4 depuis KeePassXC (Base de donnees > Parametres > Securite)`
        : `format KDBX ${major}.${minor} non supporte`,
    );
  }
  let cipher = '';
  let compressed = false;
  let masterSeed = new Uint8Array();
  let encryptionIv = new Uint8Array();
  // KDBX 3 : l'AES-KDF est la seule derivation, ses parametres sont des champs de l'en-tete.
  let kdfParams = new Map<string, VariantValue>(major === 3 ? [['$UUID', hexToBytes(KDF_AES.replaceAll('-', ''))]] : []);
  let streamId = 0;
  let streamKey = new Uint8Array();
  let streamStartBytes = new Uint8Array();
  for (;;) {
    const id = reader.u8();
    const data = reader.take(major === 3 ? reader.u16() : reader.u32());
    if (id === 0) break;
    if (id === 2) cipher = uuidOf(data);
    else if (id === 3) compressed = new ByteReader(data).u32() === 1;
    else if (id === 4) masterSeed = new Uint8Array(data);
    else if (id === 7) encryptionIv = new Uint8Array(data);
    else if (id === 11) kdfParams = parseVariantDictionary(data);
    else if (major === 3 && id === 5) kdfParams.set('S', new Uint8Array(data));
    else if (major === 3 && id === 6) kdfParams.set('R', new ByteReader(data).u64());
    else if (major === 3 && id === 8) streamKey = new Uint8Array(data);
    else if (major === 3 && id === 9) streamStartBytes = new Uint8Array(data);
    else if (major === 3 && id === 10) streamId = new ByteReader(data).u32();
  }
  if (masterSeed.length !== 32) throw new KdbxError('graine maitresse invalide');
  if (encryptionIv.length === 0) throw new KdbxError('vecteur d initialisation absent');
  return {
    header: {
      major,
      cipher,
      compressed,
      masterSeed,
      encryptionIv,
      kdfParams,
      streamId,
      streamKey,
      streamStartBytes,
      raw: bytes.subarray(0, reader.offset),
    },
    reader,
  };
}

// --- Cles -------------------------------------------------------------------

/**
 * Cle composite : SHA-256 de la concatenation des cles de chaque justificatif
 * (mot de passe hache, puis fichier-cle), exactement comme KeePass.
 */
function compositeKey(password: string, keyFile: Uint8Array | null): Uint8Array {
  const parts: Uint8Array[] = [sha256(new TextEncoder().encode(password))];
  if (keyFile !== null) parts.push(keyFileKey(keyFile));
  return sha256(concatBytes(parts));
}

/**
 * Cles composites a essayer, dans l'ordre de KeePassXC : un mot de passe VIDE accompagne d'un fichier-cle designe
 * une base protegee par le fichier-cle SEUL (aucune composante mot de passe) ; a defaut, le mot de passe vide est
 * essaye comme composante (KeePass 2 sait l'ecrire, case « Mot de passe » cochee et laissee vide).
 */
function compositeKeys(password: string, keyFile: Uint8Array | null): Uint8Array[] {
  if (password.length > 0 || keyFile === null) return [compositeKey(password, keyFile)];
  return [sha256(keyFileKey(keyFile)), compositeKey('', keyFile)];
}

/**
 * Avancement de l'essai `index` parmi `count` cles essayees l'une apres l'autre : UNE jauge pour tous les essais.
 * Chaque essai refait toute la derivation ; avant, la jauge repartait de 0 % au second et annoncait 100 % a la fin
 * d'un essai qui allait echouer. Un essai qui reussit tot termine l'import avant la fin de la jauge.
 */
function attemptProgress(onProgress: ((fraction: number) => void) | undefined, index: number, count: number): ((fraction: number) => void) | undefined {
  return onProgress === undefined ? undefined : (fraction) => onProgress((index + fraction) / count);
}

/**
 * Cle d'un fichier-cle : XML KeePass (`<Data>` base64 ou hexadecimal selon la
 * version), sinon 32 octets bruts, sinon 64 caracteres hexadecimaux, sinon
 * SHA-256 du contenu.
 */
function keyFileKey(bytes: Uint8Array): Uint8Array {
  const text = new TextDecoder().decode(bytes.subarray(0, Math.min(bytes.length, 4096)));
  const xml = /<Data[^>]*>([\s\S]*?)<\/Data>/u.exec(text);
  if (xml !== null) {
    const value = xml[1]!.replace(/\s+/gu, '');
    if (/^[0-9A-Fa-f]{64}$/u.test(value)) return hexToBytes(value);
    try {
      const decoded = base64ToBytes(value);
      if (decoded.length === 32) return decoded;
    } catch {
      throw new KdbxError('fichier-cle illisible');
    }
  }
  if (bytes.length === 32) return new Uint8Array(bytes);
  const trimmed = new TextDecoder().decode(bytes).trim();
  if (/^[0-9A-Fa-f]{64}$/u.test(trimmed)) return hexToBytes(trimmed);
  return sha256(bytes);
}

function hexToBytes(hex: string): Uint8Array {
  const out = new Uint8Array(hex.length / 2);
  for (let i = 0; i < out.length; i++) out[i] = Number.parseInt(hex.slice(i * 2, i * 2 + 2), 16);
  return out;
}

/**
 * AES-KDF de KeePass (KDBX et KeePass 1.x) : `tours` chiffrements AES-256-ECB de chaque moitie de la cle composite
 * sous la graine, puis SHA-256.
 */
async function aesKdf(seed: Uint8Array, rounds: number, composite: Uint8Array, onProgress?: (fraction: number) => void): Promise<Uint8Array> {
  if (rounds > MAX_AES_KDF_ROUNDS) throw new KdbxError('nombre de tours AES-KDF hors limites');
  if (seed.length !== 32) throw new KdbxError('graine AES-KDF invalide');
  // Un objet `ecb` de @noble/ciphers 2 ne chiffre qu'UNE fois (garde anti-reutilisation) : au 2e tour, toute base
  // AES-KDF etait refusee. La cle est developpee une fois, chaque moitie rechiffree sur place par l'AES de la meme
  // bibliotheque (3 fois plus rapide qu'un `ecb` neuf par tour, a des millions de tours).
  const expanded = aesBlock.expandKeyLE(seed);
  const block = new Uint8Array(composite);
  for (let i = 0; i < rounds; i++) {
    // A des millions de tours, la derivation figeait l'onglet des dizaines de secondes sans un signe de vie.
    if (i > 0 && i % AES_KDF_TICK === 0) {
      onProgress?.(i / rounds);
      await new Promise((resolve) => setTimeout(resolve, 0));
    }
    aesBlock.encryptBlock(expanded, block.subarray(0, 16));
    aesBlock.encryptBlock(expanded, block.subarray(16, 32));
  }
  onProgress?.(1);
  return sha256(block);
}

/** Applique la derivation declaree dans l'en-tete a la cle composite. */
async function transformKey(params: Map<string, VariantValue>, composite: Uint8Array, onProgress?: (fraction: number) => void): Promise<Uint8Array> {
  const uuid = uuidOf(requireBytes(params, '$UUID'));
  if (uuid === KDF_AES) {
    const seed = requireBytes(params, 'S');
    const rounds = requireNumber(params, 'R');
    return aesKdf(seed, rounds, composite, onProgress);
  }
  if (uuid !== KDF_ARGON2D && uuid !== KDF_ARGON2ID) throw new KdbxError('fonction de derivation inconnue');
  const memoryKib = Math.floor(requireNumber(params, 'M') / 1024);
  const iterations = requireNumber(params, 'I');
  const parallelism = requireNumber(params, 'P');
  if (memoryKib < 8 || memoryKib > MAX_ARGON2_MEMORY_KIB) throw new KdbxError('memoire Argon2 hors limites');
  if (iterations < 1 || iterations > MAX_ARGON2_ITERATIONS) throw new KdbxError('iterations Argon2 hors limites');
  if (parallelism < 1 || parallelism > 64) throw new KdbxError('parallelisme Argon2 hors limites');
  const version = params.has('V') ? requireNumber(params, 'V') : 0x13;
  const derive = uuid === KDF_ARGON2D ? argon2dAsync : argon2idAsync;
  return derive(composite, requireBytes(params, 'S'), { t: iterations, m: memoryKib, p: parallelism, dkLen: 32, version, asyncTick: 100, onProgress });
}

// --- Charge utile -----------------------------------------------------------

/** Concatene les blocs authentifies ; un bloc dont le HMAC ne correspond pas = fichier altere. */
function readHmacBlocks(reader: ByteReader, hmacBase: Uint8Array): Uint8Array {
  const blocks: Uint8Array[] = [];
  let total = 0;
  for (let index = 0; ; index++) {
    const mac = reader.take(32);
    const length = reader.u32();
    const data = reader.take(length);
    const expected = hmac(sha256, blockHmacKey(hmacBase, index), concatBytes([u64le(index), u32le(length), data]));
    if (!timingSafeEquals(mac, expected)) throw new KdbxError('bloc de donnees altere (HMAC invalide)');
    if (length === 0) break;
    total += length;
    if (total > MAX_PAYLOAD_BYTES) throw new KdbxError('charge utile trop volumineuse');
    blocks.push(new Uint8Array(data));
  }
  return concatBytes(blocks);
}

/** Blocs haches KDBX 3 : (rang, SHA-256, taille, donnees) jusqu'au bloc vide, d'empreinte nulle. */
function readHashedBlocks(reader: ByteReader): Uint8Array {
  const blocks: Uint8Array[] = [];
  let total = 0;
  for (let index = 0; ; index++) {
    if (reader.u32() !== index) throw new KdbxError('bloc de donnees hors sequence');
    const hash = reader.take(32);
    const length = reader.u32();
    const data = reader.take(length);
    if (length === 0) {
      if (hash.some((byte) => byte !== 0)) throw new KdbxError('bloc de fin altere');
      break;
    }
    if (!timingSafeEquals(hash, sha256(data))) throw new KdbxError('bloc de donnees altere (SHA-256 invalide)');
    total += length;
    if (total > MAX_PAYLOAD_BYTES) throw new KdbxError('charge utile trop volumineuse');
    blocks.push(new Uint8Array(data));
  }
  return concatBytes(blocks);
}

function decryptPayload(header: KdbxHeader, key: Uint8Array, payload: Uint8Array): Uint8Array {
  if (header.cipher === CIPHER_AES256_CBC) {
    if (header.encryptionIv.length !== 16) throw new KdbxError('vecteur d initialisation AES invalide');
    return cbc(key, header.encryptionIv).decrypt(payload);
  }
  if (header.cipher === CIPHER_CHACHA20) {
    if (header.encryptionIv.length !== 12) throw new KdbxError('nonce ChaCha20 invalide');
    return chacha20(key, header.encryptionIv, payload);
  }
  if (header.cipher === CIPHER_TWOFISH) {
    throw new KdbxError('chiffrement Twofish non supporte : reenregistrez la base en AES-256 ou ChaCha20');
  }
  throw new KdbxError('algorithme de chiffrement inconnu');
}

interface InnerHeader {
  readonly streamKey: Uint8Array;
  readonly streamId: number;
  readonly binaries: readonly Uint8Array[];
}

/** En-tete interne KDBX 4 : cle du flux des valeurs protegees + pieces jointes. */
function parseInnerHeader(reader: ByteReader): InnerHeader {
  let streamKey = new Uint8Array();
  let streamId = 0;
  const binaries: Uint8Array[] = [];
  for (;;) {
    const id = reader.u8();
    const data = reader.take(reader.u32());
    if (id === 0) break;
    if (id === 1) streamId = new ByteReader(data).u32();
    else if (id === 2) streamKey = new Uint8Array(data);
    // Premier octet = drapeaux (0x01 = protege en memoire), le reste est le contenu du fichier.
    else if (id === 3) binaries.push(new Uint8Array(data.subarray(1)));
  }
  return { streamKey, streamId, binaries };
}

// --- XML --------------------------------------------------------------------

export interface XmlNode {
  readonly tag: string;
  readonly attributes: Readonly<Record<string, string>>;
  readonly children: XmlNode[];
  text: string;
}

const EMPTY_NODE: XmlNode = { tag: '', attributes: {}, children: [], text: '' };

const XML_ENTITIES: Record<string, string> = { amp: '&', lt: '<', gt: '>', quot: '"', apos: "'" };

/** Entites XML (nommees et numeriques) d'un texte ; une entite inconnue reste telle quelle. */
export function decodeEntities(text: string): string {
  if (!text.includes('&')) return text;
  return text.replace(/&(#x?[0-9A-Fa-f]+|[a-zA-Z]+);/gu, (match, body: string) => {
    if (body.startsWith('#x') || body.startsWith('#X')) return String.fromCodePoint(Number.parseInt(body.slice(2), 16));
    if (body.startsWith('#')) return String.fromCodePoint(Number.parseInt(body.slice(1), 10));
    return XML_ENTITIES[body] ?? match;
  });
}

/** Fin de la balise ouvrante, en ignorant les chevrons places dans une valeur d'attribut. */
function findTagEnd(text: string, start: number): number {
  let quote = '';
  for (let i = start; i < text.length; i++) {
    const char = text[i]!;
    if (quote !== '') {
      if (char === quote) quote = '';
    } else if (char === '"' || char === "'") {
      quote = char;
    } else if (char === '>') {
      return i;
    }
  }
  throw new KdbxError('XML interne malforme');
}

/**
 * Analyseur XML minimal, suffisant pour le document KDBX (elements, attributs,
 * texte, CDATA, commentaires). Volontairement sans DOM : ce module reste
 * portable et testable hors navigateur, comme les autres imports (Sticky
 * Password le reutilise).
 */
export function parseXml(text: string): XmlNode {
  const root: XmlNode = { tag: '#document', attributes: {}, children: [], text: '' };
  const stack: XmlNode[] = [root];
  let cursor = 0;
  while (cursor < text.length) {
    const open = text.indexOf('<', cursor);
    if (open === -1) break;
    const top = stack[stack.length - 1]!;
    if (open > cursor) top.text += decodeEntities(text.slice(cursor, open));
    if (text.startsWith('<![CDATA[', open)) {
      const end = text.indexOf(']]>', open);
      if (end === -1) throw new KdbxError('XML interne malforme');
      top.text += text.slice(open + 9, end);
      cursor = end + 3;
      continue;
    }
    if (text.startsWith('<!--', open)) {
      const end = text.indexOf('-->', open);
      if (end === -1) throw new KdbxError('XML interne malforme');
      cursor = end + 3;
      continue;
    }
    if (text.startsWith('<?', open) || text.startsWith('<!', open)) {
      cursor = findTagEnd(text, open) + 1;
      continue;
    }
    const end = findTagEnd(text, open);
    const raw = text.slice(open + 1, end);
    cursor = end + 1;
    if (raw.startsWith('/')) {
      if (stack.length > 1) stack.pop();
      continue;
    }
    const selfClosing = raw.endsWith('/');
    const body = selfClosing ? raw.slice(0, -1) : raw;
    const tag = /^[^\s/]+/u.exec(body)?.[0] ?? '';
    const attributes: Record<string, string> = {};
    for (const match of body.slice(tag.length).matchAll(/([\w:-]+)\s*=\s*"([^"]*)"|([\w:-]+)\s*=\s*'([^']*)'/gu)) {
      attributes[(match[1] ?? match[3])!] = decodeEntities((match[2] ?? match[4])!);
    }
    const node: XmlNode = { tag, attributes, children: [], text: '' };
    top.children.push(node);
    if (!selfClosing) stack.push(node);
  }
  return root;
}

export function childrenNamed(node: XmlNode | null, tag: string): XmlNode[] {
  return (node ?? EMPTY_NODE).children.filter((child) => child.tag === tag);
}

export function childNamed(node: XmlNode | null, tag: string): XmlNode | null {
  return (node ?? EMPTY_NODE).children.find((child) => child.tag === tag) ?? null;
}

function textOf(node: XmlNode | null, tag: string): string {
  return childNamed(node, tag)?.text ?? '';
}

/**
 * Dechiffre les valeurs `Protected="True"` : KeePass les chiffre avec UN SEUL
 * flux continu (ChaCha20, ou Salsa20 en KDBX 3), dans l'ordre du document. On
 * concatene donc toutes les valeurs, on applique le flux une fois, puis on
 * redecoupe. Les pieces jointes protegees de `Meta > Binaries` (KDBX 3) sont
 * des octets, pas du texte : elles sont rendues a part.
 */
function decryptProtectedValues(root: XmlNode, streamId: number, streamKey: Uint8Array): Map<XmlNode, Uint8Array> {
  const binaries = new Map<XmlNode, Uint8Array>();
  const protectedNodes: XmlNode[] = [];
  const visit = (node: XmlNode): void => {
    if (node.attributes['Protected'] === 'True' && node.text.trim().length > 0) protectedNodes.push(node);
    for (const child of node.children) visit(child);
  };
  visit(root);
  if (protectedNodes.length === 0) return binaries;
  const parts = protectedNodes.map((node) => base64ToBytes(node.text.trim()));
  let stream: Uint8Array;
  if (streamId === INNER_STREAM_CHACHA20) {
    const derived = sha512(streamKey);
    stream = chacha20(derived.subarray(0, 32), derived.subarray(32, 44), concatBytes(parts));
  } else if (streamId === INNER_STREAM_SALSA20) {
    stream = salsa20(sha256(streamKey), SALSA20_NONCE, concatBytes(parts));
  } else {
    throw new KdbxError('flux des valeurs protegees non supporte (Salsa20 ou ChaCha20 attendu)');
  }
  const decoder = new TextDecoder();
  let offset = 0;
  for (let i = 0; i < protectedNodes.length; i++) {
    const length = parts[i]!.length;
    const clear = stream.slice(offset, offset + length);
    if (protectedNodes[i]!.tag === 'Binary') binaries.set(protectedNodes[i]!, clear);
    else protectedNodes[i]!.text = decoder.decode(clear);
    offset += length;
  }
  return binaries;
}

/** Horodatage KDBX : ISO 8601 (4.0) ou base64 d'un entier 64 bits de secondes depuis l'an 1 (4.1). */
function parseKdbxTime(raw: string): number | null {
  const value = raw.trim();
  if (value.length === 0) return null;
  if (value.includes('-') || value.includes(':')) {
    const parsed = Date.parse(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  let seconds: number;
  try {
    seconds = new ByteReader(base64ToBytes(value)).u64();
  } catch {
    return null;
  }
  // Epoque KDBX = 0001-01-01T00:00:00Z, soit 62 135 596 800 s avant l'epoque Unix.
  const millis = (seconds - 62_135_596_800) * 1000;
  return Number.isFinite(millis) && millis > 0 ? millis : null;
}

// --- Elements ---------------------------------------------------------------

function entryStrings(entry: XmlNode): Map<string, string> {
  const strings = new Map<string, string>();
  for (const node of childrenNamed(entry, 'String')) {
    const key = textOf(node, 'Key');
    if (key.length > 0) strings.set(key, textOf(node, 'Value'));
  }
  return strings;
}

/**
 * Cles des chaines protegees en memoire : `Protected="True"` dans une base
 * (dechiffrees en amont, l'attribut reste), `ProtectInMemory="True"` dans un
 * export XML en clair.
 */
function protectedStringKeys(entry: XmlNode): Set<string> {
  const keys = new Set<string>();
  for (const node of childrenNamed(entry, 'String')) {
    const attributes = childNamed(node, 'Value')?.attributes ?? {};
    if (attributes['Protected'] === 'True' || attributes['ProtectInMemory'] === 'True') keys.add(textOf(node, 'Key'));
  }
  return keys;
}

/** Contenu d'une piece jointe designee par l'element `<Value>` de son `<Binary>`. */
type BinaryResolver = (value: XmlNode | null) => Uint8Array | undefined;

/** Pieces jointes d'une base : `Ref` = rang dans l'en-tete interne. */
function binaryByRef(binaries: readonly Uint8Array[]): BinaryResolver {
  return (value) => {
    const reference = value?.attributes['Ref'];
    return reference === undefined ? undefined : binaries[Number.parseInt(reference, 10)];
  };
}

// --- References de champ ({REF:...}) ------------------------------------------

/** UUID d'un element `<Entry>` (base64 de 16 octets) en hexadecimal majuscule, `''` si illisible. */
function entryUuidHex(entry: XmlNode): string {
  try {
    const bytes = base64ToBytes(textOf(entry, 'UUID').trim());
    return bytes.length === 16 ? [...bytes].map((byte) => byte.toString(16).padStart(2, '0')).join('').toUpperCase() : '';
  } catch {
    return '';
  }
}

function buildReferenceIndex(root: XmlNode, recycleBinUuid: string): ReferenceIndex {
  const entries: ReferenceTarget[] = [];
  const byUuid = new Map<string, ReferenceTarget>();
  const visit = (group: XmlNode, live: boolean): void => {
    for (const entry of childrenNamed(group, 'Entry')) {
      const target: ReferenceTarget = { uuid: entryUuidHex(entry), strings: entryStrings(entry), live };
      entries.push(target);
      if (target.uuid.length > 0 && !byUuid.has(target.uuid)) byUuid.set(target.uuid, target);
    }
    for (const child of childrenNamed(group, 'Group')) {
      visit(child, live && !(recycleBinUuid.length > 0 && textOf(child, 'UUID').trim() === recycleBinUuid));
    }
  };
  visit(root, true);
  return { entries, byUuid };
}

function parseKdbxEntry(entry: XmlNode, folderPath: readonly string[], binaryOf: BinaryResolver, index: ReferenceIndex): ImportedEntry {
  const uuid = entryUuidHex(entry);
  const { strings, targets, unresolved } = resolveEntryReferences(entryStrings(entry), uuid, index);
  // Lien vers chaque entree reprise dont une reference a tire une valeur (« Elements lies »), jamais vers soi-meme.
  const linkedSourceIds = [...targets].filter((target) => target !== uuid && index.byUuid.get(target)?.live === true);
  const extras: string[] = [];
  let totpSecret = '';
  for (const key of TOTP_STRING_KEYS) {
    const raw = strings.get(key);
    if (raw === undefined || raw.trim().length === 0) continue;
    const normalized = normalizeTotpSecret(raw);
    if (totpSecret === '' && normalized.secret.length > 0) totpSecret = normalized.secret;
    else if (normalized.extraNote.length > 0) extras.push(normalized.extraNote);
  }
  // Chaines hors champs natifs = champs personnalises KeePass ; « protegee en memoire » devient « masque ».
  const customFields: EntryCustomField[] = [];
  const protectedKeys = protectedStringKeys(entry);
  for (const [key, value] of strings) {
    if (NATIVE_STRING_KEYS.has(key) || TOTP_STRING_KEYS.includes(key) || value.length === 0) continue;
    customFields.push({ name: key, value, hidden: protectedKeys.has(key) });
  }
  // KeePass separe ses etiquettes par « ; » (KeePassXC) ou « , » (KeePass 2).
  const tags = parseTagList(textOf(entry, 'Tags'));

  // Historique : KeePass conserve l'entree complete a chaque revision ; seul le
  // mot de passe nous interesse, date de sa derniere modification.
  const passwordHistory: PasswordHistoryEntry[] = [];
  const currentPassword = strings.get('Password') ?? '';
  for (const old of childrenNamed(childNamed(entry, 'History'), 'Entry')) {
    const password = entryStrings(old).get('Password') ?? '';
    const changedAt = parseKdbxTime(textOf(childNamed(old, 'Times'), 'LastModificationTime'));
    if (password.length === 0 || password === currentPassword) continue;
    if (passwordHistory.some((item) => item.password === password)) continue;
    passwordHistory.push({ password, changedAt: changedAt ?? 0 });
  }
  passwordHistory.sort((a, b) => b.changedAt - a.changedAt);

  // Pose du mot de passe courant : la plus ancienne revision de la derniere suite qui le porte deja (l'historique va
  // du plus ancien au plus recent), sinon la derniere modification de l'entree. Borne haute : sans historique (desactive
  // ou ecrete), la vraie pose est anterieure ou egale -- jamais un mot de passe change ne passe pour perime.
  let passwordChangedAt = parseKdbxTime(textOf(childNamed(entry, 'Times'), 'LastModificationTime'));
  for (const old of childrenNamed(childNamed(entry, 'History'), 'Entry').reverse()) {
    if ((entryStrings(old).get('Password') ?? '') !== currentPassword) break;
    passwordChangedAt = parseKdbxTime(textOf(childNamed(old, 'Times'), 'LastModificationTime')) ?? passwordChangedAt;
  }

  // Echeance (« Expires » coche) : le jour UTC de `ExpiryTime`. KeePass l'ecrit en UTC ; une echeance posee a minuit
  // a l'est de Greenwich tombe la veille -- prevenu un jour trop tot, jamais trop tard.
  const times = childNamed(entry, 'Times');
  const expiresAt = /^true$/iu.test(textOf(times, 'Expires').trim()) ? parseKdbxTime(textOf(times, 'ExpiryTime')) : null;
  const passwordExpiry = expiresAt === null ? null : new Date(expiresAt).toISOString().slice(0, 10);

  const attachments: ImportedAttachment[] = [];
  for (const binary of childrenNamed(entry, 'Binary')) {
    const fileName = textOf(binary, 'Key');
    const bytes = binaryOf(childNamed(binary, 'Value'));
    if (fileName.length === 0 || bytes === undefined || bytes.length === 0) continue;
    attachments.push({ fileName, bytes });
  }

  const url = strings.get('URL') ?? '';
  const username = strings.get('UserName') ?? '';
  const type: VaultItemType = currentPassword.length > 0 || username.length > 0 || url.length > 0 ? 'login' : 'note';
  return {
    type,
    name: strings.get('Title') || url || username || 'Sans nom',
    username,
    password: currentPassword,
    uris: url.length > 0 ? [url] : [],
    notes: joinNotes([strings.get('Notes') ?? '', ...extras]),
    totpSecret,
    favorite: false,
    ...(folderPath.length > 0 ? { folderPath } : {}),
    ...(passwordHistory.length > 0 ? { passwordHistory } : {}),
    ...(passwordExpiry !== null ? { passwordExpiry } : {}),
    ...(passwordChangedAt !== null ? { passwordChangedAt } : {}),
    ...(attachments.length > 0 ? { attachments } : {}),
    ...(customFields.length > 0 ? { customFields } : {}),
    ...(tags.length > 0 ? { tags } : {}),
    ...(uuid.length > 0 ? { sourceId: uuid } : {}),
    ...(linkedSourceIds.length > 0 ? { linkedSourceIds } : {}),
    ...(unresolved > 0 ? { unresolvedReferences: unresolved } : {}),
  };
}

function collectGroup(
  group: XmlNode,
  path: readonly string[],
  recycleBinUuid: string,
  binaryOf: BinaryResolver,
  index: ReferenceIndex,
  out: ImportedEntry[],
  folders: (readonly string[])[],
): void {
  if (recycleBinUuid.length > 0 && textOf(group, 'UUID').trim() === recycleBinUuid) return;
  // Groupe nomme = dossier, meme vide : KeePass et Bitwarden recreent toute l'arborescence.
  if (path.length > 0) folders.push(path);
  for (const entry of childrenNamed(group, 'Entry')) out.push(parseKdbxEntry(entry, path, binaryOf, index));
  for (const child of childrenNamed(group, 'Group')) {
    const name = textOf(child, 'Name').trim();
    collectGroup(child, name.length > 0 ? [...path, name] : path, recycleBinUuid, binaryOf, index, out, folders);
  }
}

// --- Point d'entree ---------------------------------------------------------

/**
 * Le dechiffrement de ce format accepte-t-il un fichier-cle ? Oui pour les bases KeePass (`.kdbx`, `.kdb`), dont
 * `ImportPasswordRequiredError` porte le nom : l'ecran d'import propose alors le fichier-cle a cote du mot de passe.
 */
export function acceptsKeyFile(format: string): boolean {
  return /^KeePass\b/u.test(format);
}

/** Signature KDBX (2/3/4) : reconnue avant toute tentative de lecture. */
export function isKdbxFile(bytes: Uint8Array): boolean {
  if (bytes.length < 12) return false;
  const reader = new ByteReader(bytes);
  return reader.u32() === KDBX_SIGNATURE_1 && reader.u32() === KDBX_SIGNATURE_2;
}

export interface KdbxImportOptions {
  /** Mot de passe de la base — jamais conserve, jamais journalise. */
  readonly password?: string;
  /** Fichier-cle optionnel (`.keyx` / `.key`). */
  readonly keyFile?: Uint8Array | null;
  /** Avancement de la derivation de cle (0..1) : seule etape longue, jusqu'a des dizaines de secondes. */
  readonly onProgress?: (fraction: number) => void;
}

/**
 * Dechiffre une base KDBX 4 ou 3.1 et en extrait les entrees (groupes conserves comme
 * chemins de dossiers, historique des mots de passe et pieces jointes inclus).
 * La corbeille KeePass est ignoree.
 *
 * @throws {ImportPasswordRequiredError} aucun mot de passe fourni
 * @throws {ImportPasswordInvalidError} HMAC d'en-tete invalide = mauvais mot de passe / fichier-cle
 * @throws {KdbxError} format, algorithme ou structure non supportes
 */
export async function parseKdbxFile(bytes: Uint8Array, options: KdbxImportOptions = {}): Promise<ImportedEntry[]> {
  return (await readKdbxFile(bytes, options)).entries;
}

/** Entrees d'un document KeePass et chemins de TOUS ses groupes nommes, vides compris (hors corbeille). */
export interface KeePassDocument {
  readonly entries: ImportedEntry[];
  readonly folders: (readonly string[])[];
}

/** Comme `parseKdbxFile`, avec l'arborescence des groupes : un groupe vide reste un dossier. */
export async function readKdbxFile(bytes: Uint8Array, options: KdbxImportOptions = {}): Promise<KeePassDocument> {
  if (!isKdbxFile(bytes)) throw new KdbxError('signature KeePass absente');
  if (options.password === undefined) throw new ImportPasswordRequiredError('KeePass (.kdbx)');
  const candidates = compositeKeys(options.password, options.keyFile ?? null);
  for (const [index, composite] of candidates.entries()) {
    try {
      return await readKdbxWithKey(bytes, composite, attemptProgress(options.onProgress, index, candidates.length));
    } catch (cause) {
      if (!(cause instanceof ImportPasswordInvalidError) || index === candidates.length - 1) throw cause;
    }
  }
  throw new ImportPasswordInvalidError('KeePass (.kdbx)');
}

/** Lecture d'une base KDBX sous UNE cle composite ; une cle fausse leve `ImportPasswordInvalidError`. */
async function readKdbxWithKey(bytes: Uint8Array, composite: Uint8Array, onProgress?: (fraction: number) => void): Promise<KeePassDocument> {
  const { header, reader } = parseHeader(bytes);
  const transformed = await transformKey(header.kdfParams, composite, onProgress);
  const seeded = concatBytes([header.masterSeed, transformed]);
  const cipherKey = sha256(seeded);
  if (header.major === 3) return readKdbx3Payload(header, cipherKey, bytes.subarray(reader.offset));
  const hmacBase = sha512(concatBytes([seeded, Uint8Array.of(1)]));

  if (!timingSafeEquals(reader.take(32), sha256(header.raw))) throw new KdbxError('en-tete altere');
  // Le HMAC d'en-tete depend de la cle : c'est LUI qui distingue un mauvais mot
  // de passe d'un fichier corrompu — aucun dechiffrement n'est tente avant.
  const headerHmac = reader.take(32);
  if (!timingSafeEquals(headerHmac, hmac(sha256, blockHmacKey(hmacBase, HEADER_HMAC_INDEX), header.raw))) {
    throw new ImportPasswordInvalidError('KeePass (.kdbx)');
  }

  const authenticated = readHmacBlocks(reader, hmacBase);
  let plain: Uint8Array;
  try {
    const payload = decryptPayload(header, cipherKey, authenticated);
    plain = header.compressed ? await decompress(payload, 'gzip', MAX_PAYLOAD_BYTES) : payload;
  } catch (cause) {
    // Les blocs sont authentifies : un echec ici = fichier malforme, pas un mauvais
    // mot de passe. On ne laisse pas remonter le message brut de la bibliotheque.
    if (cause instanceof KdbxError) throw cause;
    throw new KdbxError('donnees illisibles apres dechiffrement');
  }
  const inner = new ByteReader(plain);
  const innerHeader = parseInnerHeader(inner);
  const xml = parseXml(new TextDecoder().decode(plain.subarray(inner.offset)));
  decryptProtectedValues(xml, innerHeader.streamId, innerHeader.streamKey);
  return readDocument(childNamed(xml, 'KeePassFile') ?? xml, binaryByRef(innerHeader.binaries));
}

/**
 * Charge d'une base KDBX 3 : aucun HMAC, ce sont les octets de controle en tete
 * de la charge dechiffree qui distinguent un mauvais mot de passe ; puis blocs
 * haches, GZip, XML, et `HeaderHash` pour l'en-tete qu'aucun HMAC n'a couvert.
 */
async function readKdbx3Payload(header: KdbxHeader, cipherKey: Uint8Array, encrypted: Uint8Array): Promise<KeePassDocument> {
  if (header.streamStartBytes.length !== 32) throw new KdbxError('octets de controle absents');
  let decrypted: Uint8Array;
  try {
    decrypted = decryptPayload(header, cipherKey, encrypted);
  } catch (cause) {
    if (cause instanceof KdbxError) throw cause;
    // Remplissage CBC invalide : la cle est fausse (ou le fichier abime, indiscernable sans HMAC, comme dans KeePass).
    throw new ImportPasswordInvalidError('KeePass (.kdbx)');
  }
  if (!timingSafeEquals(decrypted.subarray(0, 32), header.streamStartBytes)) throw new ImportPasswordInvalidError('KeePass (.kdbx)');
  const blocks = readHashedBlocks(new ByteReader(decrypted.subarray(32)));
  let plain: Uint8Array;
  try {
    plain = header.compressed ? await decompress(blocks, 'gzip', MAX_PAYLOAD_BYTES) : blocks;
  } catch {
    throw new KdbxError('donnees illisibles apres dechiffrement');
  }
  const xml = parseXml(new TextDecoder().decode(plain));
  const file = childNamed(xml, 'KeePassFile') ?? xml;
  const headerHash = textOf(childNamed(file, 'Meta'), 'HeaderHash').trim();
  if (headerHash.length > 0 && !timingSafeEquals(base64ToBytes(headerHash), sha256(header.raw))) throw new KdbxError('en-tete altere');
  const pool = await metaBinaryPool(file, decryptProtectedValues(file, header.streamId, header.streamKey));
  return readDocument(file, (value) => {
    const reference = value?.attributes['Ref'];
    return reference === undefined ? undefined : pool.get(reference);
  });
}

/**
 * Entrees d'un document `<KeePassFile>` deja en clair : chemin commun a la base
 * KDBX et a l'export XML, pour qu'ils ne divergent jamais.
 */
function readDocument(file: XmlNode, binaryOf: BinaryResolver): KeePassDocument {
  const root = childNamed(file, 'Root');
  if (root === null) throw new KdbxError('document XML sans racine KeePass');
  const recycleBinUuid = textOf(childNamed(file, 'Meta'), 'RecycleBinUUID').trim();
  const entries: ImportedEntry[] = [];
  const folders: (readonly string[])[] = [];
  const index = buildReferenceIndex(root, recycleBinUuid);
  // Le groupe racine porte le nom de la base : ses entrees restent a la racine du coffre.
  for (const group of childrenNamed(root, 'Group')) {
    collectGroup(group, [], recycleBinUuid, binaryOf, index, entries, folders);
  }
  for (const entry of childrenNamed(root, 'Entry')) entries.push(parseKdbxEntry(entry, [], binaryOf, index));
  return { entries, folders };
}

// --- KeePass 1.x (.kdb) -----------------------------------------------------

/** Second mot de la signature d'une base KeePass 1.x (`PWM_DBSIG_2`), le premier etant celui des KDBX. */
const KDB_SIGNATURE_2 = 0xb54bfb65;
const KDB_HEADER_BYTES = 124;
const KDB_FLAG_RIJNDAEL = 2;
const KDB_FLAG_TWOFISH = 8;
/** Version 3.x (`PWM_DBVER_DW`) ; l'octet de poids faible n'est pas critique (KeePass et KeePassXC l'ignorent). */
const KDB_VERSION = 0x0003_0000;
const KDB_VERSION_MASK = 0xffff_ff00;
const KDB_END_OF_RECORD = 0xffff;
/** Echeance « n'expire jamais » de KeePass 1.x (`g_pwTimeNever`, `PwManager.cpp`). */
const KDB_NEVER_EXPIRES = '2999-12-28';
/** Windows-1252 de 0x80 a 0x9F : le reste du codage coincide avec Latin-1. */
const WINDOWS_1252_HIGH = '€\u0081‚ƒ„…†‡ˆ‰Š‹Œ\u008dŽ\u008f\u0090‘’“”•–—˜™š›œ\u009džŸ';

/** Signature KeePass 1.x (`.kdb`) : reconnue avant toute tentative de lecture. */
export function isKdbFile(bytes: Uint8Array): boolean {
  if (bytes.length < 12) return false;
  const reader = new ByteReader(bytes);
  return reader.u32() === KDBX_SIGNATURE_1 && reader.u32() === KDB_SIGNATURE_2;
}

/**
 * Codages essayes pour le mot de passe : UTF-8 (KeePass 1.x recent), puis le codage ANSI Windows-1252 des anciennes
 * versions quand il differe -- comme KeePassXC (`KeePass1Reader`), sans quoi une vieille base au mot de passe accentue
 * serait refusee avec le bon mot de passe.
 */
function kdbPasswordEncodings(password: string): Uint8Array[] {
  const utf8 = new TextEncoder().encode(password);
  const legacy: number[] = [];
  for (const char of password) {
    const code = char.codePointAt(0)!;
    const high = WINDOWS_1252_HIGH.indexOf(char);
    if (high !== -1) legacy.push(0x80 + high);
    else if (code < 0x80 || (code >= 0xa0 && code <= 0xff)) legacy.push(code);
    else return [utf8];
  }
  return timingSafeEquals(utf8, Uint8Array.from(legacy)) ? [utf8] : [utf8, Uint8Array.from(legacy)];
}

/**
 * Cle maitresse KeePass 1.x (`PwManager::SetMasterKey`) : SHA-256 du mot de passe SEUL (un seul hachage, pas la cle
 * composite KDBX), la cle du fichier-cle seul, ou SHA-256 des deux. Fichier-cle : 32 octets bruts, 64 caracteres
 * hexadecimaux, sinon SHA-256 du contenu (KeePass 1.x ne connait pas le fichier-cle XML).
 */
function kdbMasterKey(password: Uint8Array, keyFile: Uint8Array | null): Uint8Array {
  if (keyFile === null) return sha256(password);
  const text = keyFile.length === 64 ? new TextDecoder().decode(keyFile) : '';
  const fileKey = keyFile.length === 32 ? new Uint8Array(keyFile) : /^[0-9A-Fa-f]{64}$/u.test(text) ? hexToBytes(text) : sha256(keyFile);
  return password.length === 0 ? fileKey : sha256(concatBytes([sha256(password), fileKey]));
}

/** Enregistrement KeePass 1.x : champs (type 16 bits, taille 32 bits, donnees) jusqu'au champ de fin. */
function readKdbRecord(reader: ByteReader): Map<number, Uint8Array> {
  const fields = new Map<number, Uint8Array>();
  for (;;) {
    const type = reader.u16();
    const data = reader.take(reader.u32());
    if (type === KDB_END_OF_RECORD) return fields;
    fields.set(type, data);
  }
}

function kdbString(data: Uint8Array | undefined): string {
  if (data === undefined) return '';
  const end = data.indexOf(0);
  return new TextDecoder().decode(end === -1 ? data : data.subarray(0, end));
}

function kdbNumber(data: Uint8Array | undefined, size: 2 | 4): number | null {
  if (data === undefined || data.length < size) return null;
  const reader = new ByteReader(data);
  return size === 2 ? reader.u16() : reader.u32();
}

/** Date KeePass 1.x sur 5 octets (annee 14 bits, mois, jour, heure, minute, seconde), heure locale sans fuseau. */
function kdbTime(data: Uint8Array | undefined): { readonly day: string; readonly time: number } | null {
  if (data === undefined || data.length !== 5) return null;
  const [b0, b1, b2, b3, b4] = data as unknown as [number, number, number, number, number];
  const year = (b0 << 6) | (b1 >> 2);
  const month = ((b1 & 0x03) << 2) | (b2 >> 6);
  const day = (b2 >> 1) & 0x1f;
  if (year < 1 || month < 1 || month > 12 || day < 1) return null;
  const time = Date.UTC(year, month - 1, day, ((b2 & 0x01) << 4) | (b3 >> 4), ((b3 & 0x0f) << 2) | (b4 >> 6), b4 & 0x3f);
  return { day: `${String(year).padStart(4, '0')}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`, time };
}

/**
 * Flux de metadonnees de KeePass 1.x (etat de l'arborescence, icones, reglages) : une entree technique que KeePass
 * cache (`_IsMetaStream`). Importee, elle ferait apparaitre des « Meta-Info » dans le coffre.
 */
function isKdbMetaStream(fields: ReadonlyMap<number, Uint8Array>): boolean {
  return (
    (fields.get(0x0e)?.length ?? 0) > 0 &&
    kdbString(fields.get(0x08)).length > 0 &&
    kdbString(fields.get(0x0d)) === 'bin-stream' &&
    kdbString(fields.get(0x04)) === 'Meta-Info' &&
    kdbString(fields.get(0x06)) === 'SYSTEM' &&
    kdbString(fields.get(0x05)) === '$' &&
    kdbNumber(fields.get(0x03), 4) === 0
  );
}

/**
 * Groupes puis entrees d'une base KeePass 1.x dechiffree. Les groupes sont ecrits dans l'ordre de l'arbre avec leur
 * niveau : le parent est le dernier groupe du niveau au-dessus (chemin exact, sans l'ambiguite du `\` de l'export
 * CSV). Les references `{REF:...}` (que KeePass 1.x resout, `SprEngine.cpp`) passent par le meme resolveur que KDBX.
 */
function readKdbContent(plain: Uint8Array, groupCount: number, entryCount: number): KeePassDocument {
  const reader = new ByteReader(plain);
  const groupPaths = new Map<number, readonly string[]>();
  const folders: (readonly string[])[] = [];
  const stack: (readonly string[])[] = [];
  for (let i = 0; i < groupCount; i++) {
    const fields = readKdbRecord(reader);
    const id = kdbNumber(fields.get(0x01), 4);
    const level = kdbNumber(fields.get(0x08), 2) ?? 0;
    if (id === null) throw new KdbxError('groupe sans identifiant');
    if (level > stack.length) throw new KdbxError('arborescence des groupes invalide');
    const parent = level > 0 ? stack[level - 1]! : [];
    const name = kdbString(fields.get(0x02)).trim();
    const path = name.length > 0 ? [...parent, name] : parent;
    stack.length = level;
    stack.push(path);
    groupPaths.set(id, path);
    if (name.length > 0) folders.push(path);
  }

  const records: { readonly fields: Map<number, Uint8Array>; readonly target: ReferenceTarget }[] = [];
  for (let i = 0; i < entryCount; i++) {
    const fields = readKdbRecord(reader);
    if (isKdbMetaStream(fields)) continue;
    const uuidBytes = fields.get(0x01);
    const uuid = uuidBytes?.length === 16 ? [...uuidBytes].map((byte) => byte.toString(16).padStart(2, '0')).join('').toUpperCase() : '';
    const strings = new Map<string, string>([
      ['Title', kdbString(fields.get(0x04))],
      ['UserName', kdbString(fields.get(0x06))],
      ['Password', kdbString(fields.get(0x07))],
      ['URL', kdbString(fields.get(0x05))],
      ['Notes', kdbString(fields.get(0x08))],
    ]);
    records.push({ fields, target: { uuid, strings, live: true } });
  }
  const byUuid = new Map<string, ReferenceTarget>();
  for (const { target } of records) if (target.uuid.length > 0 && !byUuid.has(target.uuid)) byUuid.set(target.uuid, target);
  const index: ReferenceIndex = { entries: records.map((record) => record.target), byUuid };

  const entries = records.map(({ fields, target }): ImportedEntry => {
    const { strings, targets, unresolved } = resolveEntryReferences(target.strings, target.uuid, index);
    const linkedSourceIds = [...targets].filter((linked) => linked !== target.uuid && byUuid.has(linked));
    const url = strings.get('URL') ?? '';
    const username = strings.get('UserName') ?? '';
    const password = strings.get('Password') ?? '';
    const folderPath = groupPaths.get(kdbNumber(fields.get(0x02), 4) ?? -1) ?? [];
    const expiry = kdbTime(fields.get(0x0c));
    const passwordChangedAt = kdbTime(fields.get(0x0a))?.time;
    const attachment = fields.get(0x0e);
    return {
      type: password.length > 0 || username.length > 0 || url.length > 0 ? 'login' : 'note',
      name: strings.get('Title') || url || username || 'Sans nom',
      username,
      password,
      uris: url.length > 0 ? [url] : [],
      notes: strings.get('Notes') ?? '',
      totpSecret: '',
      favorite: false,
      ...(folderPath.length > 0 ? { folderPath } : {}),
      ...(expiry !== null && expiry.day !== KDB_NEVER_EXPIRES ? { passwordExpiry: expiry.day } : {}),
      ...(passwordChangedAt !== undefined ? { passwordChangedAt } : {}),
      ...(attachment !== undefined && attachment.length > 0
        ? { attachments: [{ fileName: kdbString(fields.get(0x0d)).trim() || 'piece-jointe', bytes: new Uint8Array(attachment) }] }
        : {}),
      ...(target.uuid.length > 0 ? { sourceId: target.uuid } : {}),
      ...(linkedSourceIds.length > 0 ? { linkedSourceIds } : {}),
      ...(unresolved > 0 ? { unresolvedReferences: unresolved } : {}),
    };
  });
  return { entries, folders };
}

/**
 * Dechiffre une base KeePass 1.x (`.kdb`, format de KeePass 1.x et KeePassX 0.4) : en-tete fixe de 124 octets,
 * AES-KDF (graine et tours de l'en-tete), AES-256-CBC, puis SHA-256 du contenu compare a l'en-tete -- c'est LUI qui
 * detecte un mauvais mot de passe (aucun HMAC dans ce format). Twofish est refuse avec son message.
 *
 * @throws {ImportPasswordRequiredError} aucun mot de passe fourni
 * @throws {ImportPasswordInvalidError} mot de passe / fichier-cle faux (ou base abimee, indiscernable, comme dans KeePass)
 * @throws {KdbxError} format, algorithme ou structure non supportes
 */
export async function readKdbFile(bytes: Uint8Array, options: KdbxImportOptions = {}): Promise<KeePassDocument> {
  if (!isKdbFile(bytes)) throw new KdbxError('signature KeePass 1.x absente');
  if (options.password === undefined) throw new ImportPasswordRequiredError('KeePass 1.x (.kdb)');
  const reader = new ByteReader(bytes);
  reader.take(8);
  const flags = reader.u32();
  if ((reader.u32() & KDB_VERSION_MASK) >>> 0 !== KDB_VERSION) throw new KdbxError('version KeePass 1.x non supportee');
  if ((flags & KDB_FLAG_TWOFISH) !== 0) throw new KdbxError('chiffrement Twofish non supporte : reenregistrez la base en AES dans KeePass 1.x');
  if ((flags & KDB_FLAG_RIJNDAEL) === 0) throw new KdbxError('algorithme de chiffrement inconnu');
  const masterSeed = reader.take(16);
  const iv = reader.take(16);
  const groupCount = reader.u32();
  const entryCount = reader.u32();
  const contentsHash = reader.take(32);
  const transformSeed = reader.take(32);
  const rounds = reader.u32();
  const encrypted = bytes.subarray(KDB_HEADER_BYTES);
  if (encrypted.length === 0 || encrypted.length % 16 !== 0) throw new KdbxError('fichier tronque');
  const encodings = kdbPasswordEncodings(options.password);
  for (const [index, password] of encodings.entries()) {
    const onProgress = attemptProgress(options.onProgress, index, encodings.length);
    const key = sha256(concatBytes([masterSeed, await aesKdf(transformSeed, rounds, kdbMasterKey(password, options.keyFile ?? null), onProgress)]));
    let plain: Uint8Array;
    try {
      plain = cbc(key, iv).decrypt(encrypted);
    } catch {
      continue; // remplissage CBC invalide : cle fausse
    }
    if (timingSafeEquals(sha256(plain), contentsHash)) return readKdbContent(plain, groupCount, entryCount);
  }
  throw new ImportPasswordInvalidError('KeePass 1.x (.kdb)');
}

// --- Export XML en clair ----------------------------------------------------

/** Plafond d'une piece jointe decompressee d'un export XML (meme ordre que la base). */
const MAX_XML_BINARY_BYTES = 64 * 1024 * 1024;

/** Le texte est un export « KeePass XML (2.x) » : racine `<KeePassFile>`. */
export function isKeePassXml(text: string): boolean {
  return /^[\s\uFEFF]*(?:<\?xml[^>]*>\s*)?(?:<!--[\s\S]*?-->\s*)*<KeePassFile[\s>]/u.test(text);
}

/** Contenu d'un `<Binary>` d'export : base64, compresse GZip si `Compressed="True"`. */
async function xmlBinaryBytes(node: XmlNode): Promise<Uint8Array> {
  const raw = base64ToBytes(node.text.replace(/\s+/gu, ''));
  return node.attributes['Compressed'] === 'True' ? decompress(raw, 'gzip', MAX_XML_BINARY_BYTES) : raw;
}

/**
 * Reserve `Meta > Binaries` indexee par `ID` (export XML, base KDBX 3) ; une
 * piece jointe protegee d'une base y est deja dechiffree par le flux.
 */
async function metaBinaryPool(file: XmlNode, decrypted: ReadonlyMap<XmlNode, Uint8Array> = new Map()): Promise<Map<string, Uint8Array>> {
  const pool = new Map<string, Uint8Array>();
  for (const node of childrenNamed(childNamed(childNamed(file, 'Meta'), 'Binaries'), 'Binary')) {
    const id = node.attributes['ID'];
    if (id !== undefined) pool.set(id, decrypted.get(node) ?? (await xmlBinaryBytes(node)));
  }
  return pool;
}

/**
 * Import d'un export **KeePass XML (2.x)** (KeePass 2 « Fichier > Exporter »,
 * `keepassxc-cli export --format xml`, KeeWeb, Strongbox) : le meme document
 * que dans une base, mais en clair — aucun mot de passe a demander.
 *
 * Les valeurs protegees y sont ecrites en clair avec `ProtectInMemory="True"`
 * (elles restent masquees chez nous). Une valeur `Protected="True"` est le XML
 * interne d'une base, chiffre par son flux : il est REFUSE, jamais importe en
 * charabia. Pieces jointes : la reserve `Meta > Binaries` (`ID`, `Compressed`)
 * designee par `Ref`, ou le contenu en ligne des anciens exports.
 */
export async function parseKeePassXml(text: string): Promise<ImportedEntry[]> {
  return (await readKeePassXml(text)).entries;
}

/** Comme `parseKeePassXml`, avec l'arborescence des groupes (`readKdbxFile`). */
export async function readKeePassXml(text: string): Promise<KeePassDocument> {
  const file = childNamed(parseXml(text), 'KeePassFile');
  if (file === null) throw new KdbxError('export XML sans racine KeePassFile');
  const encrypted = (node: XmlNode): boolean =>
    (node.attributes['Protected'] === 'True' && node.text.trim().length > 0) || node.children.some(encrypted);
  if (encrypted(file)) {
    throw new KdbxError('XML aux valeurs encore chiffrees : exportez depuis KeePass au format « KeePass XML (2.x) » ou importez le .kdbx');
  }
  // Reserve `Meta > Binaries` indexee par `ID`, et contenus en ligne des anciens exports.
  const pool = await metaBinaryPool(file);
  const inline = new Map<XmlNode, Uint8Array>();
  const collectInline = async (node: XmlNode): Promise<void> => {
    // L'historique n'apporte que des mots de passe : ses pieces jointes ne sont pas lues.
    if (node.tag === 'History') return;
    if (node.tag === 'Binary') {
      const value = childNamed(node, 'Value');
      if (value !== null && value.attributes['Ref'] === undefined && value.text.trim().length > 0) inline.set(value, await xmlBinaryBytes(value));
    }
    for (const child of node.children) await collectInline(child);
  };
  await collectInline(childNamed(file, 'Root') ?? file);
  return readDocument(file, (value) => {
    if (value === null) return undefined;
    const reference = value.attributes['Ref'];
    return reference === undefined ? inline.get(value) : pool.get(reference);
  });
}
